// src/components/Login.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import axios from 'axios';
import './Login.css';

// Backend API base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


// Type definitions for API responses
interface LoginResponse {
  success: boolean;
  message?: string;
  user?: {
    user_id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface RegisterResponse {
  success: boolean;
  message?: string;
  user_id?: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
}

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fadeOut, setFadeOut] = useState(false); // NEW: Track fade-out state
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Forgot password states
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  
  // Create account fields
  const [newEmail, setNewEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Whitelist state - fetched from backend
  const [whitelist, setWhitelist] = useState<{ domains: string[]; accounts: string[] }>({ domains: [], accounts: [] });
  const [whitelistLoaded, setWhitelistLoaded] = useState(false);
  
  // Fetch whitelist on component mount
  useEffect(() => {
    const fetchWhitelist = async () => {
      try {
        const response = await axios.get<{ success: boolean; domains?: string[]; accounts?: string[] }>(`${API_BASE_URL}/auth/whitelist`);
        if (response.data.success) {
          setWhitelist({
            domains: response.data.domains || [],
            accounts: response.data.accounts || []
          });
        }
      } catch (err) {
        console.error('Failed to fetch whitelist:', err);
      } finally {
        setWhitelistLoaded(true);
      }
    };
    fetchWhitelist();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email: email,
        password: password
      });

      const data = response.data as LoginResponse;
      if (data.success) {
        // Success
        setSuccess(true);
        
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Start fade-out animation after 2 seconds
        setTimeout(() => {
          setFadeOut(true);
          
          // Call success callback after fade-out completes
          setTimeout(() => {
            if (onLoginSuccess) {
              onLoginSuccess();
            }
          }, 1000); // 1000ms for fade-out animation
        }, 2000); // Show success message for 2 seconds
      } else {
        setError(data.message || 'Login failed. Please try again.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.data?.requires_verification) {
        // User needs to verify email - show resend option
        setError(error.response.data.message);
        setUnverifiedEmail(error.response.data.email || email);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Login failed. Please check your connection and try again.');
      }
      setLoading(false);
    }
  };
  
  // State for unverified email
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');
  const [resendingVerification, setResendingVerification] = useState(false);
  
  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    
    setResendingVerification(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-verification`, {
        email: unverifiedEmail,
        origin_url: window.location.origin
      });
      
      const data = response.data as { success: boolean; message: string };
      if (data.success) {
        setSuccessMessage(data.message);
        setUnverifiedEmail('');
      } else {
        setError(data.message || 'Failed to resend verification email.');
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCreateAccount = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    setError('');
    
    // Validation
    if (!newEmail.trim()) {
      setError('Please enter an email address.');
      return;
    }
    
    // Check email against whitelist fetched from backend
    const isAllowedDomain = whitelist.domains.some(domain => newEmail.toLowerCase().endsWith(domain.toLowerCase()));
    const isWhitelistedEmail = whitelist.accounts.some(email => newEmail.toLowerCase() === email.toLowerCase());
    
    if (!isAllowedDomain && !isWhitelistedEmail) {
      setError('Account creation is currently restricted to approved healthcare organization email addresses only.');
      return;
    }
    
    if (!firstName.trim()) {
      setError('Please enter your first name.');
      return;
    }
    
    if (!lastName.trim()) {
      setError('Please enter your last name.');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        email: newEmail,
        first_name: firstName,
        last_name: lastName,
        password: newPassword,
        origin_url: window.location.origin
      });

      const data = response.data as RegisterResponse;
      if (data.success) {
        setSuccessMessage('Account successfully created! Please sign in with your credentials.');
        setIsCreatingAccount(false);
        setNewEmail('');
        setFirstName('');
        setLastName('');
        setNewPassword('');
        setConfirmPassword('');
        setLoading(false);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setError(data.message || 'Account creation failed. Please try again.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Account creation failed. Please check your connection and try again.');
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setIsForgotPassword(true);
    setError('');
    setForgotEmail('');
    setForgotPasswordStatus('idle');
  };

  const handleForgotPasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!forgotEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }
    
    setForgotPasswordStatus('loading');
    setError('');
    
    try {
      const response = await axios.post<ForgotPasswordResponse>(`${API_BASE_URL}/auth/forgot-password`, {
        email: forgotEmail
      });
      
      if (response.data.success) {
        setForgotPasswordStatus('success');
      } else {
        setForgotPasswordStatus('error');
        setError(response.data.message || 'Failed to send reset email.');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setForgotPasswordStatus('error');
      setError(error.response?.data?.message || 'Failed to send reset email. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setForgotEmail('');
    setForgotPasswordStatus('idle');
    setError('');
  };

  const handleContactSupport = () => {
    // Create a custom modal-like alert with clickable phone number
    const modal = document.createElement('div');
    modal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: white; padding: 24px 32px; border-radius: 12px; text-align: center; max-width: 320px; box-shadow: 0 4px 20px rgba(0,0,0,0.3);">
          <h3 style="margin: 0 0 16px 0; color: #1e293b; font-size: 18px;">Contact CareShift Support</h3>
          <a href="tel:+12142335271" style="display: inline-block; font-size: 20px; font-weight: 600; color: #14b8a6; text-decoration: none; padding: 12px 24px; border: 2px solid #14b8a6; border-radius: 8px; margin-bottom: 16px; transition: all 0.2s;">(+1) 214-233-5271</a>
          <br/>
          <button id="close-support-modal" style="margin-top: 12px; padding: 8px 24px; background: #64748b; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    
    // Close on button click or backdrop click
    modal.querySelector('#close-support-modal')?.addEventListener('click', () => modal.remove());
    modal.querySelector('div')?.addEventListener('click', (e) => {
      if (e.target === modal.querySelector('div')) modal.remove();
    });
  };

  const toggleToCreateAccount = () => {
    setIsCreatingAccount(true);
    setIsForgotPassword(false);
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
  };

  const toggleToSignIn = () => {
    setIsCreatingAccount(false);
    setIsForgotPassword(false);
    setError('');
    setNewEmail('');
    setFirstName('');
    setLastName('');
    setNewPassword('');
    setConfirmPassword('');
  };

  if (success) {
    return (
      <div className={`success-message-container ${fadeOut ? 'fade-out' : ''}`}>
        <div className="success-icon-wrapper">
          <svg viewBox="0 0 24 24" className="success-checkmark">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <h2 className="success-title">Welcome Back!</h2>
        <p className="success-subtitle">Login successful. Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-background-dots" />
      
      <div className="login-card">
        <div className="login-logo-section">
          <div className="login-title-row">
            <img src="/logo.png" alt="CareShift Logo" className="login-logo-image" />
            <h1 className="login-title">CareShift</h1>
          </div>
          <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
        </div>

        {successMessage && (
          <div className="login-success-banner">
            {successMessage}
          </div>
        )}

        {isForgotPassword ? (
          // FORGOT PASSWORD FORM
          <>
            <div className="forgot-password-header">
              <button 
                type="button" 
                onClick={handleBackToLogin}
                className="back-to-login-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                </svg>
                Back to Login
              </button>
            </div>
            
            {forgotPasswordStatus === 'success' ? (
              <div className="forgot-password-success">
                <div className="success-icon-circle">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <h3>Check Your Email</h3>
                <p>If an account exists with this email, a password reset link has been sent.</p>
                <p className="forgot-password-hint">Didn't receive the email? Check your spam folder or try again.</p>
                <button 
                  type="button" 
                  onClick={handleBackToLogin}
                  className="login-submit-btn"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPasswordSubmit} className="login-form">
                <h3 className="forgot-password-title">Reset Your Password</h3>
                <p className="forgot-password-description">
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>
                
                <div className="login-form-group">
                  <label className="login-form-label" htmlFor="forgotEmail">
                    Email
                  </label>
                  <div className="login-input-wrapper">
                    <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    <input 
                      type="email" 
                      id="forgotEmail" 
                      className="login-form-input" 
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="login-error-message">
                    {error}
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="login-submit-btn"
                  disabled={forgotPasswordStatus === 'loading'}
                >
                  {forgotPasswordStatus === 'loading' ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}
          </>
        ) : !isCreatingAccount ? (
          // SIGN IN FORM
          <>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-form-group">
                <label className="login-form-label" htmlFor="email">
                  Email
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <input 
                    type="email" 
                    id="email" 
                    className="login-form-input" 
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="login-form-group">
                <label className="login-form-label" htmlFor="password">
                  Password
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    id="password" 
                    className="login-form-input" 
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button 
                    type="button" 
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    )}
                  </button>
                </div>
                <div className="login-forgot-password">
                  <button 
                    type="button"
                    onClick={handleForgotPassword}
                    className="login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {error && (
                <div className="login-error-message">
                  {error}
                  {unverifiedEmail && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      style={{
                        display: 'block',
                        marginTop: '0.75rem',
                        padding: '0.5rem 1rem',
                        background: '#0d9488',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: resendingVerification ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        width: '100%'
                      }}
                    >
                      {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                    </button>
                  )}
                </div>
              )}

              <button 
                type="submit" 
                className="login-submit-btn"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="login-create-account-text">
              Don't have an account?{' '}
              <button 
                type="button"
                onClick={toggleToCreateAccount}
                className="login-create-account-link"
              >
                Create Account
              </button>
            </div>

            <div className="login-divider">or</div>

            <div className="login-help-text">
              Need help?{' '}
              <button 
                type="button"
                onClick={handleContactSupport}
                className="login-support-link"
              >
                Contact Support
              </button>
            </div>
          </>
        ) : (
          // CREATE ACCOUNT FORM
          <>
            <form onSubmit={handleCreateAccount} className="login-form">
              <div className="login-form-group">
                <label className="login-form-label" htmlFor="firstName">
                  First Name
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <input 
                    type="text" 
                    id="firstName" 
                    className="login-form-input" 
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                </div>
              </div>

              <div className="login-form-group">
                <label className="login-form-label" htmlFor="lastName">
                  Last Name
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <input 
                    type="text" 
                    id="lastName" 
                    className="login-form-input" 
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="login-form-group">
                <label className="login-form-label" htmlFor="newEmail">
                  Email
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <input 
                    type="email" 
                    id="newEmail" 
                    className="login-form-input" 
                    placeholder="Enter your email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-form-group">
                <label className="login-form-label" htmlFor="newPassword">
                  Password
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input 
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword" 
                    className="login-form-input" 
                    placeholder="Create a password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button 
                    type="button" 
                    className="login-password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    )}
                  </button>
                </div>
                {newPassword && (
                  <div className={`password-requirement ${newPassword.length >= 8 ? 'met' : 'unmet'}`}>
                    {newPassword.length >= 8 ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        Minimum requirements met!
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                        Password must be at least 8 characters
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="login-form-group">
                <label className="login-form-label" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <div className="login-input-wrapper">
                  <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  <input 
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword" 
                    className="login-form-input" 
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button 
                    type="button" 
                    className="login-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    )}
                  </button>
                </div>
                {confirmPassword && (
                  <div className={`password-requirement ${confirmPassword && newPassword === confirmPassword ? 'met' : 'unmet'}`}>
                    {confirmPassword && newPassword === confirmPassword ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        Passwords match!
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                        Passwords must match
                      </>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className="login-error-message">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="login-submit-btn"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="login-create-account-text">
              Already have an account?{' '}
              <button 
                type="button"
                onClick={toggleToSignIn}
                className="login-create-account-link"
              >
                Sign In
              </button>
            </div>

            <div className="login-divider">or</div>

            <div className="login-help-text">
              Need help?{' '}
              <button 
                type="button"
                onClick={handleContactSupport}
                className="login-support-link"
              >
                Contact Support
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


export default Login;